import { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldAlert } from 'lucide-react';
import { BadgeHelpIcon } from '@/components/BadgeKey';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * YieldTrapBadge — Round 85 (Feb 2026).
 *
 * Renders a small "Yield Trap" warning chip when the composite signal
 * score is ≥ 2 from /api/stocks/:ticker/yield-trap. Composite checks:
 *   • Yield ≥ 8%
 *   • Payout > 100% (REIT) / > 90% (non-REIT)
 *   • Recent dividend cut
 *   • 1Y price decline > 25%
 *   • Days-to-cover ≥ 5 (heavy short interest)
 *
 * The tooltip lists each tripped signal so the user understands WHY.
 * Self-hides when score < 2 (renders nothing).
 *
 * Pass `compact` to render only the icon (no text) for use inside
 * dense card grids.
 */
export default function YieldTrapBadge({ ticker, assetClass, compact = false }) {
  const [data, setData] = useState(null);

  // Round 93j (Jun 2026) — fixed-rate assets (baby bonds, preferred
  // shares, depositary shares) have legitimately high yields when
  // they trade below par. Trap-detection growth-math doesn't apply;
  // the Stock Modal's "Asset Class" educational notice explains the
  // mechanics. Skip the badge entirely for these to avoid stacking
  // a red trap warning on top of the calm amber notice.
  const isFixedRateAsset = (
    assetClass === 'baby_bond' || assetClass === 'preferred_share'
  );

  useEffect(() => {
    if (!ticker || isFixedRateAsset) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/stocks/${ticker}/yield-trap`, { withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { /* swallow — badge just hides */ });
    return () => { cancelled = true; };
  }, [ticker, isFixedRateAsset]);

  if (isFixedRateAsset) return null;
  if (!data || !data.is_trap) return null;

  const tooltip = [
    `Yield-Trap score ${data.score}/5`,
    '',
    ...(data.signals || []).map((s) => `• ${s.label} — ${s.detail}`),
  ].join('\n');

  return (
    <span className="inline-flex items-center gap-1" data-testid={`yield-trap-wrap-${ticker}`}>
      <span
        className={`inline-flex items-center gap-1 rounded-md border border-rose-300/70 bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 cursor-help ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]'}`}
        title={tooltip}
        data-testid={`yield-trap-badge-${ticker}`}
      >
        <ShieldAlert className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        {!compact && <span className="font-semibold uppercase tracking-wide">Yield Trap</span>}
        <span className="font-mono font-bold">{data.score}</span>
      </span>
      {!compact && <BadgeHelpIcon badgeKey="yield_trap" />}
    </span>
  );
}
